import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Button from "@/v2-backoffice-client/components/button"
import UserUpdateForm from "@/v2-backoffice-client/user/update/UserUpdateForm"
import { useUserUpdateMutation } from "@/v2-backoffice-client/user/update/user.update.mutation"
import { useUserActiveProjectMutation } from "@/v2-backoffice-client/user/activeProject/user.activeProject.mutation"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

export type UserUpdateDialogProps = DialogProps & {
  user: UserFragment
}

// The dialog is only the shell: UserUpdateForm (mounted inside
// Dialog.Content, so it resets by remounting each open) owns the fields and
// hands a finished payload up here, where the two mutations live.
function UserUpdateDialog(props: UserUpdateDialogProps) {
  const { user, open, onOpenChange, ...dialogProps } = props

  const [updateUser, { loading, error }] = useUserUpdateMutation()
  const [setActiveProject, { loading: activeLoading, error: activeError }] =
    useUserActiveProjectMutation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Update User</Dialog.Title>
        </Dialog.Header>

        <ErrorMessage
          className="my-4 text-center"
          error={error ?? activeError}
        />

        <UserUpdateForm
          id="user-edit-form"
          user={user}
          onSubmit={async (data) => {
            try {
              const result = await updateUser({
                variables: {
                  input: {
                    Id: user.Id,
                    FirstName: data.FirstName,
                    LastName: data.LastName,
                    Email: data.Email,
                    Language: data.Language,
                  },
                },
              })
              // errorPolicy is "all" app-wide, so a rejected update resolves
              // instead of throwing — closing here would swallow the error
              // banner above.
              if (result.errors?.length) return

              if (data.active) {
                const activeResult = await setActiveProject({
                  variables: {
                    input: { UserId: user.Id, ...data.active },
                  },
                })
                if (activeResult.errors?.length) return
              }

              onOpenChange?.(false)
            } catch {
              // Network errors surface via the hook error states above; keep
              // the dialog open so the operator can retry.
            }
          }}
        />

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading || activeLoading}
            form="user-edit-form"
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UserUpdateDialog
